import { LocalStorageService } from "./LocalStorageService";

/**
 * Beyaz etiket markası — artık configuration.json'dan değil API'den (DB) gelir.
 *
 * Neden: publish sunucudaki configuration.json'ı koruduğu (paketten silerek) için marka
 * değişiklikleri deploy'a binmiyor, logo/favicon eski kalıyordu. Marka veri olunca bu hata sınıfı
 * yapısal olarak ortadan kalkar.
 *
 * İki uygulamayı da besler: HIS (yönetim) ve hasta portalı (online sonuç) — başlıkları ayrıdır.
 *
 * Dayanıklılık kuralları:
 *   - Son bilinen marka localStorage'a yazılır → sonraki açılışta ANINDA boyanır (FOUC yok).
 *   - API ulaşılamazsa önce cache, o da yoksa GÖMÜLÜ VARSAYILAN (bizim HIS markamız) kullanılır.
 *     Uygulama hiçbir koşulda markasız açılmaz.
 *   - Kiracının yüklemediği her varlık için bizim varsayılanımıza düşülür (kısmi beyaz etiket mümkün).
 */

const CACHE_KEY = "branding";

/** Gömülü varsayılan: kurulumda hiç marka yoksa / API yoksa uygulama bununla açılır. */
export const DEFAULT_BRANDING = {
    displayName: "",
    title: { tr: "HIS · eCloudPACS", en: "HIS · eCloudPACS" },
    description: { tr: "HIS · eCloudPACS", en: "HIS · eCloudPACS" },
    onlineTitle: { tr: "Online Sonuç", en: "Online Result" },
    onlineDescription: { tr: "", en: "" },
    assets: {},
    headerColor: null,
    headerLogoBackground: null,
    loginLogoBackground: null,
    onlineHours: { tr: [], en: [] },
    onlineContact: { tr: [], en: [] },
    patientNotePopup: false,
    posEnable: false,
};

/** Kiracı yüklememişse kullanılacak kendi varlıklarımız (build içinde gelir). */
export const OWN_ASSETS = {
    headerLogo: "/logo-his.svg",          // koyu header için
    loginLogo: "/logo-his-light.svg",     // beyaz kart için
    loginBackground: "/login-bg.webp",
    favicon: "/favicon-his.svg",
};

/** Marka rengi (header rengi verilmemişse). */
export const BRAND_SLATE = "#34495e";

let current = DEFAULT_BRANDING;

const apiBase = () => window.AppSettings?.server?.api ?? "";

/** API göreli yol döner (/api/PublicBranding/Asset/12?v=hash) → mutlaklaştır. */
const absolute = (url) => (url?.startsWith("/api/") ? `${apiBase()}${url}` : url);

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeCache(branding) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
    } catch {
        /* kota dolabilir; marka cache'i kritik değil */
    }
}

export const Branding = {
    /** Kiracı adı (yoksa boş). */
    displayName: () => current.displayName || "",

    /** Varlık URL'i: kiracının yüklediği → yoksa bizim gömülü varsayılanımız. */
    asset: (kind) => absolute(current.assets?.[kind]) || OWN_ASSETS[kind],

    /** Yalnız kiracının yüklediği varlık (bizimkine düşmez); yoksa null. */
    tenantAsset: (kind) => (current.assets?.[kind] ? absolute(current.assets[kind]) : null),

    /** Header zemin rengi — kiracı belirlemediyse marka slate'i. */
    headerColor: () => current.headerColor || BRAND_SLATE,

    /**
     * Logonun arkasına konacak zemin rengi (yoksa null).
     * Kiracının logosu şeffaf değilse (beyaz zeminli PNG) header'da/kartta yama gibi durur;
     * o rengin kutusuna alınınca kasıtlı görünür. Yalnız KİRACININ logosu için uygulanır.
     */
    logoBackground: (kind) => {
        if (!current.assets?.[kind]) return null;   // bizim logomuz → zemin kutusu yok
        return kind === "headerLogo"
            ? current.headerLogoBackground || null
            : current.loginLogoBackground || null;
    },

    /** module: 'his' | 'online' */
    title: (module, lang) => {
        const source = module === "online" ? current.onlineTitle : current.title;
        const fallback = module === "online" ? DEFAULT_BRANDING.onlineTitle : DEFAULT_BRANDING.title;
        return source?.[lang] || source?.tr || fallback.tr;
    },

    description: (module, lang) => {
        const source = module === "online" ? current.onlineDescription : current.description;
        return source?.[lang] || source?.tr || "";
    },

    /** Hasta portalı çalışma saatleri — satır listesi (boş satırlar korunur). */
    onlineHours: (lang) => current.onlineHours?.[lang] || current.onlineHours?.tr || [],

    /** Hasta portalı iletişim bilgisi — satır listesi (boş satır = şube ayracı). */
    onlineContact: (lang) => current.onlineContact?.[lang] || current.onlineContact?.tr || [],

    /**
     * Kiracıya özel özellik bayrakları — eski configuration.json → his.*
     * name: 'patientNotePopup' | 'posEnable'
     */
    feature: (name) => Boolean(current[name]),
};

/** Başlık, meta açıklama ve favicon'u markadan uygula. */
export function applyBranding(module = "his") {
    const lang = LocalStorageService.getLanguage("tr");

    document.title = Branding.title(module, lang);
    document
        ?.querySelector('meta[name="description"]')
        ?.setAttribute("content", Branding.description(module, lang));

    const favicon = Branding.asset("favicon");
    document.querySelector('link[rel="icon"]')?.setAttribute("href", favicon);
    document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute("href", favicon);
}

/**
 * Markayı yükler. Önce cache ile boyar (anında), sonra API'den doğrular.
 * API başarısızsa cache/gömülü varsayılanla devam eder — uygulama açılışını ASLA bloklamaz.
 */
export async function loadBranding(module = "his") {
    const cached = readCache();
    if (cached) {
        current = cached;
        applyBranding(module);
    }

    try {
        const res = await fetch(`${apiBase()}/api/PublicBranding/Get`, { cache: "no-store" });
        if (res.ok) {
            const body = await res.json();
            const data = body?.data ?? body;
            if (data) {
                current = {
                    displayName: data.displayName ?? "",
                    title: data.title ?? DEFAULT_BRANDING.title,
                    description: data.description ?? DEFAULT_BRANDING.description,
                    onlineTitle: data.onlineTitle ?? DEFAULT_BRANDING.onlineTitle,
                    onlineDescription: data.onlineDescription ?? DEFAULT_BRANDING.onlineDescription,
                    assets: data.assets ?? {},
                    headerColor: data.headerColor ?? null,
                    headerLogoBackground: data.headerLogoBackground ?? null,
                    loginLogoBackground: data.loginLogoBackground ?? null,
                    onlineHours: data.onlineHours ?? DEFAULT_BRANDING.onlineHours,
                    onlineContact: data.onlineContact ?? DEFAULT_BRANDING.onlineContact,
                    patientNotePopup: Boolean(data.patientNotePopup),
                    posEnable: Boolean(data.posEnable),
                };
                writeCache(current);
            }
        }
    } catch {
        // ağ yok / API kapalı → cache ya da gömülü varsayılan zaten yüklü
    }

    applyBranding(module);
    window.AppBranding = current;   // hata ayıklama ve non-React tüketiciler için
    return current;
}
